/**
 * Time formatting for recipient-local minutes-from-midnight values.
 * Minutes may exceed 1439 — that is "past midnight, same run day" and
 * renders as "HH:mm +1d". Danish labels (15/10/2026) live in clock/dk-label —
 * exact for a run date, never today's offsets applied to another day.
 * For Media Devoted | Created By Ciro Autiero
 */
export function hhmmToMinutes(value: string): number | null {
  const match = /^(\d{1,2}):(\d{2})$/.exec(value.trim());
  if (!match) return null;
  const minutes = Number(match[1]) * 60 + Number(match[2]);
  return minutes < 1440 ? minutes : null;
}

export function minutesToHHMM(min: number): string {
  const wrapped = ((min % 1440) + 1440) % 1440;
  const h = String(Math.floor(wrapped / 60)).padStart(2, '0');
  const m = String(wrapped % 60).padStart(2, '0');
  return `${h}:${m}`;
}

/** "02:30 +1d" for past-midnight minutes, plain "HH:mm" otherwise. */
export function minutesLabel(min: number): string {
  return min > 1439 ? `${minutesToHHMM(min)} +1d` : minutesToHHMM(min);
}

/** A timezone's UTC offset in minutes at a given instant. */
export function tzOffsetMinutes(timeZone: string, at: Date): number {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone,
    hour12: false,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  }).formatToParts(at);
  const get = (type: string) =>
    Number(parts.find((p) => p.type === type)?.value ?? 0);
  const asUtc = Date.UTC(
    get('year'),
    get('month') - 1,
    get('day'),
    get('hour') % 24,
    get('minute'),
  );
  return Math.round((asUtc - at.getTime()) / 60000);
}
